import { Fragment } from 'react';
import { LEGAL_LINKS } from '@reflex/shared/external-links';
import { cn } from '@reflex/ui/lib/utils';

/**
 * The consent sentence's punctuation, resolved once at module load.
 *
 * `Intl.ListFormat` owns the joining so the sentence stays grammatical at any
 * list length — a hand-rolled Oxford comma reads correctly at three items and
 * wrongly ("terms of service, and privacy policy") at two, which is exactly
 * the arity a policy removal would produce. `formatToParts` is what lets the
 * elements come back as links rather than plain text: we format positional
 * tokens, then swap each `element` part for the anchor it stands for.
 */
type LegalConsentPart =
  | { type: 'literal'; value: string }
  | { type: 'link'; link: (typeof LEGAL_LINKS)[number] };

const legalConsentParts: LegalConsentPart[] = new Intl.ListFormat('en', {
  style: 'long',
  type: 'conjunction',
})
  .formatToParts(LEGAL_LINKS.map((_link, index) => String(index)))
  .flatMap((part): LegalConsentPart[] => {
    if (part.type === 'literal') return [{ type: 'literal', value: part.value }];
    // Every non-literal part is one of the positional tokens we just handed
    // in, so this always resolves. Dropping an unresolvable token instead of
    // trusting it keeps a future mismatch to a missing link rather than an
    // `undefined.href` that white-screens the whole sign-in page.
    const link = LEGAL_LINKS[Number(part.value)];
    return link ? [{ type: 'link', link }] : [];
  });

interface LegalConsentLineProps {
  /**
   * What the user is about to do, as the head of the sentence. Defaults to the
   * sign-in screen's wording; the MCP approval screen says "By authorizing".
   */
  lead?: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * The "By continuing you agree to the …" line, rendered from `LEGAL_LINKS`.
 *
 * Every pre-sign-in surface that records a `legal_accepted_at` has to render
 * this, and the record is only honest if the two never drift — so the sentence
 * lives here rather than being retyped per screen. Today that is the web
 * sign-in screen and the MCP authorization screen.
 */
export function LegalConsentLine({
  lead = 'By continuing you agree to the',
  className,
  'data-testid': testId = 'auth-legal-consent',
}: LegalConsentLineProps) {
  return (
    <p
      data-testid={testId}
      className={cn('pt-1 text-center text-2xs text-muted-foreground', className)}
    >
      {lead}{' '}
      {legalConsentParts.map((part, index) =>
        part.type === 'literal' ? (
          <Fragment key={index}>{part.value}</Fragment>
        ) : (
          <a
            key={index}
            href={part.link.href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-muted-foreground/40 underline-offset-4 transition-colors hover:text-foreground hover:decoration-foreground/60"
          >
            {part.link.inlineLabel}
          </a>
        ),
      )}
      .
    </p>
  );
}
